import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import Emitter from '@/services/event-emitter';
import type {RootState} from '@/store';
import {isUserLoggedIn} from '@Libraries/user.library';
import type {AiImageModelOption} from '@Panels/ai-image-panel/ai-image-panel.types';
import {DEFAULT_SELECTED_AI_IMAGE_MODEL} from '@Panels/ai-image-panel/ai-image-panel.types';

export interface UserPreferences {
  hideSocialMediaRemoveAccountConfirmation: boolean;
  isSpellCheckEnabled: boolean;
  isAutosaveEnabled: boolean;
  isSpellCheckAvailable: boolean;
  areAlignmentGuidesEnabled: boolean;
  selectedAiImageModelId?: AiImageModelOption;
  selectedAiVoiceProfileId: number | null;
}

interface StateProps extends UserPreferences {
  isAutosavePreferenceUpdating: boolean;
  areAlignmentGuidesPreferenceUpdating: boolean;
  isSpellCheckPreferenceUpdating: boolean;
}

const initialState: StateProps = {
  hideSocialMediaRemoveAccountConfirmation: false,
  isSpellCheckEnabled: false,
  isAutosaveEnabled: true,
  isSpellCheckAvailable: false,
  areAlignmentGuidesEnabled: true,
  isAutosavePreferenceUpdating: false,
  areAlignmentGuidesPreferenceUpdating: false,
  isSpellCheckPreferenceUpdating: false,
  selectedAiImageModelId: DEFAULT_SELECTED_AI_IMAGE_MODEL,
  selectedAiVoiceProfileId: null,
};

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    updateUserPreferences(state, action: PayloadAction<UserPreferences>) {
      state.hideSocialMediaRemoveAccountConfirmation = action.payload.hideSocialMediaRemoveAccountConfirmation;
      state.isSpellCheckEnabled = action.payload.isSpellCheckEnabled;
      state.isAutosaveEnabled = action.payload.isAutosaveEnabled;
      state.areAlignmentGuidesEnabled = action.payload.areAlignmentGuidesEnabled;
      state.isSpellCheckAvailable = action.payload.isSpellCheckAvailable;
      state.selectedAiImageModelId = action.payload.selectedAiImageModelId;
      state.selectedAiVoiceProfileId = action.payload.selectedAiVoiceProfileId;
    },
    updateIsSpellCheckEnabled(state, action: PayloadAction<boolean>) {
      state.isSpellCheckEnabled = action.payload;
    },
    updateIsAutosaveEnabled(state, action: PayloadAction<boolean>) {
      state.isAutosaveEnabled = action.payload;
    },
    updateAreAlignmentGuidesEnabled(state, action: PayloadAction<boolean>) {
      state.areAlignmentGuidesEnabled = action.payload;
    },
    updateIsAutosavePreferenceUpdating(state, action: PayloadAction<boolean>) {
      state.isAutosavePreferenceUpdating = action.payload;
    },
    updateAreAlignmentGuidesPreferenceUpdating(state, action: PayloadAction<boolean>) {
      state.areAlignmentGuidesPreferenceUpdating = action.payload;
    },
    updateIsSpellCheckPreferenceUpdating(state, action: PayloadAction<boolean>) {
      state.isSpellCheckPreferenceUpdating = action.payload;
    },
    updateSelectedAiImageModelId(state, action: PayloadAction<AiImageModelOption | undefined>) {
      state.selectedAiImageModelId = action.payload;
    },
    updateSelectedAiVoiceProfileId(state, action: PayloadAction<number | null>) {
      state.selectedAiVoiceProfileId = action.payload;
    },
  },
});

export const {
  updateUserPreferences,
  updateIsSpellCheckPreferenceUpdating,
  updateIsSpellCheckEnabled,
  updateIsAutosaveEnabled,
  updateSelectedAiImageModelId,
} = userPreferencesSlice.actions;
export const userPreferencesReducer = userPreferencesSlice.reducer;

export const setSpellCheckEnabledState = async (val: boolean): Promise<void> => {
  window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateIsSpellCheckPreferenceUpdating(true));
  try {
    await window.PMW.writeLocal('spellcheck/enableDisableSpellCheck', {
      enable: val ? 'true' : 'false',
    });
    window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateIsSpellCheckEnabled(val));
  } finally {
    window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateIsSpellCheckPreferenceUpdating(false));
  }
};

export const updateAreAlignmentGuidesEnabled = async (val: boolean): Promise<void> => {
  window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateAreAlignmentGuidesPreferenceUpdating(true));
  try {
    if (isUserLoggedIn()) {
      await window.PMW.writeLocal('user/updateAreAlignmentGuidesEnabled', {
        val: val ? '1' : '0',
      });
    }
    window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateAreAlignmentGuidesEnabled(val));
    Emitter.emit('userPreferences:areAlignmentGuidesEnabledUpdated');
  } finally {
    window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateAreAlignmentGuidesPreferenceUpdating(false));
  }
};

export const updateSelectedAiImageModelIdBackend = async (option: AiImageModelOption): Promise<void> => {
  return window.PMW.writeLocal('user/updateLastSelectedImageOption', {
    aiImageModelOption: option.valueOf(),
  }) as Promise<void>;
};

export const updateSelectedAiVoiceProfileId = (profileId: number | null): void => {
  void window.PMW.writeLocal('user/updateLastSelectedAiVoiceProfileId', {
    profileId,
  });
  window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateSelectedAiVoiceProfileId(profileId));
};

export const updateIsAutosaveEnabledForUser = async (val: boolean): Promise<void> => {
  window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateIsAutosavePreferenceUpdating(true));
  try {
    if (isUserLoggedIn()) {
      await window.PMW.writeLocal('user/updateIsAutosaveEnabled', {
        val: val ? '1' : '0',
      });
    }
    window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateIsAutosaveEnabled(val));
  } finally {
    window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateIsAutosavePreferenceUpdating(false));
  }
};

export const getIsSpellCheckEnabled = (state: RootState): boolean => {
  return state.userPreferences.isSpellCheckAvailable && state.userPreferences.isSpellCheckEnabled;
};

export const fetchAndSyncUserPreferences = async (): Promise<void> => {
  const userPreferences = (await window.PMW.readLocal('user/getUserPreferences')) as UserPreferences;
  window.PMW.redux.store.dispatch(userPreferencesSlice.actions.updateUserPreferences(userPreferences));
};
